import { GridCellParams } from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';
import { DataGridTable, useColumnsWithCellState, useGridRefs } from '@schooly/components/data-grid';
import { useCallback, useMemo } from 'react';

import { AttendanceScrollArrows } from '../AttendanceScrollArrows';
import { AttendanceDayHeaderRenderer, createAttendanceColumns } from './attendanceColumns';
import { AttendanceStyling, getColWidths } from './attendanceStyling';
import { AttendanceCellState, AttendanceGridColumn, AttendanceRow } from './types';

const PINNED_COLUMNS_COUNT = 1;

export type AttendanceGridProps<TRow extends AttendanceRow> = {
  rows: TRow[];
  gridColumns: AttendanceGridColumn[];
  styling: AttendanceStyling;
  primaryHeaderLabel: string;
  renderDayHeader: AttendanceDayHeaderRenderer;
  getCellState: (cellId: string) => AttendanceCellState | undefined;
  withArrows?: boolean;
  onPageLeft?: () => void;
  onPageRight?: () => void;
};

export const AttendanceGrid = <TRow extends AttendanceRow>({
  rows,
  gridColumns,
  styling,
  primaryHeaderLabel,
  renderDayHeader,
  getCellState,
  withArrows = true,
  onPageLeft,
  onPageRight,
}: AttendanceGridProps<TRow>) => {
  const { containerRef, apiRef } = useGridRefs();

  const isCellEditable = useCallback((params: GridCellParams) => {
    const row = params.row as AttendanceRow;
    const cell = row.cells?.[params.field];
    return !!cell?.editable;
  }, []);

  const columns = useColumnsWithCellState<TRow, AttendanceCellState, unknown>(
    useMemo(
      () =>
        createAttendanceColumns<TRow>(
          gridColumns,
          primaryHeaderLabel,
          styling.view,
          renderDayHeader,
        ),
      [gridColumns, primaryHeaderLabel, styling.view, renderDayHeader],
    ),
    getCellState,
  );

  const colWidths = useMemo(
    () => getColWidths(styling.view, gridColumns),
    [styling.view, gridColumns],
  );

  return (
    <Box ref={containerRef} sx={styling.gridContainerSx}>
      {withArrows && !!gridColumns.length && (
        <AttendanceScrollArrows
          apiRef={apiRef}
          containerRef={containerRef}
          pinnedColumns={PINNED_COLUMNS_COUNT}
          colWidths={colWidths}
          onPageLeft={onPageLeft}
          onPageRight={onPageRight}
        />
      )}
      <DataGridTable
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        rowHeight={styling.dataGridProps.rowHeight}
        columnHeaderHeight={styling.dataGridProps.columnHeaderHeight}
        isCellEditable={isCellEditable}
        columnsToPin={styling.dataGridProps.columnsToPin}
        noNavigationOnFirstCol={styling.dataGridProps.noNavigationOnFirstCol}
        headerCellSelector={styling.dataGridProps.headerCellSelector}
        withDataGridHover={styling.dataGridProps.withDataGridHover}
        slotProps={styling.slotProps}
        sx={styling.dataGridSx}
      />
    </Box>
  );
};
